import type { User, Transaction, PromptPaySettings, ApiResponse, StaffMember, SubscriptionTier, SUBSCRIPTION_PLANS } from '@tangtung/shared';

// Response types for subscription and staff
interface SubscriptionStatus {
    tier: SubscriptionTier;
    is_active: boolean;
    started_at: string | null;
    expires_at: string | null;
    trial_used: boolean;
    days_remaining: number | null;
}

interface SubscriptionPlan {
    tier: SubscriptionTier;
    name: string;
    price: number;
    features: string[];
}

interface FlexMessage {
    type: string;
    hero?: any;
    body?: any;
    footer?: any;
}

interface InviteResponse {
    token: string;
    invite_url: string;
    expires_in: number;
    expires_at: string;
    shop_name: string;
    flex_message: FlexMessage;
}

interface JoinStoreResponse {
    success: boolean;
    message: string;
    shop_name: string;
    owner_name: string;
    redirect_to_oa?: boolean;
    oa_url?: string;
}

interface StaffListResponse {
    staff: StaffMember[];
    shop_name: string;
}

interface StoreInfoResponse {
    is_staff: boolean;
    store: {
        name: string;
        owner_name: string;
        owner_picture: string | null;
        owner_line_user_id: string;
        promptpay_number: string | null;
        promptpay_name: string | null;
    } | null;
}

// API Base URL - Auto-detect environment
const getApiUrl = () => {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:8787';
    // All deployed environments use the same API
    return 'https://tangtungdev-api.greenslime2009.workers.dev';
};
const API_BASE_URL = getApiUrl();

class ApiClient {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    // Generic request method (public for flexible API calls)
    async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                // Prefer Thai message over English error code
                return { data: null, error: errorData.message || errorData.error || `HTTP ${response.status}` };
            }

            const data = await response.json();
            return { data, error: null };
        } catch (error) {
            console.error('API request failed:', error);
            return { data: null, error: String(error) };
        }
    }

    // =====================
    // USER METHODS
    // =====================

    async getUser(lineUserId: string): Promise<ApiResponse<User>> {
        return this.request<User>(`/api/users/${lineUserId}`);
    }

    async createUser(data: {
        line_user_id: string;
        display_name?: string;
        picture_url?: string;
    }): Promise<ApiResponse<User>> {
        return this.request<User>('/api/users', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateUser(
        lineUserId: string,
        data: Partial<User>
    ): Promise<ApiResponse<User>> {
        return this.request<User>(`/api/users/${lineUserId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async uploadImage(file: File): Promise<{ success: boolean; url: string }> {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${this.baseUrl}/api/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Image upload failed');
        }

        return await response.json();
    }

    // =====================
    // PROMPTPAY METHODS
    // =====================

    async getPromptPaySettings(lineUserId: string): Promise<ApiResponse<PromptPaySettings>> {
        const result = await this.getUser(lineUserId);
        if (!result.data) {
            return { data: null, error: result.error };
        }

        return {
            data: {
                number: result.data.promptpay_number || '',
                name: result.data.promptpay_name || '',
                type: 'phone',
            },
            error: null,
        };
    }

    async savePromptPaySettings(
        lineUserId: string,
        settings: PromptPaySettings
    ): Promise<ApiResponse<User>> {
        return this.updateUser(lineUserId, {
            promptpay_number: settings.number,
            promptpay_name: settings.name,
        });
    }

    // =====================
    // TRANSACTION METHODS
    // =====================

    async getTransactions(
        lineUserId: string,
        limit: number = 50
    ): Promise<ApiResponse<Transaction[]>> {
        return this.request<Transaction[]>(
            `/api/transactions/${lineUserId}?limit=${limit}`
        );
    }

    async getTodayTransactions(lineUserId: string): Promise<ApiResponse<Transaction[]>> {
        return this.request<Transaction[]>(`/api/transactions/${lineUserId}/today`);
    }

    async getMonthlyTransactions(
        lineUserId: string,
        yearMonth: string
    ): Promise<ApiResponse<Transaction[]>> {
        return this.request<Transaction[]>(
            `/api/transactions/${lineUserId}/monthly/${yearMonth}`
        );
    }

    async createTransaction(data: {
        line_user_id: string;
        amount: number;
        payment_type?: 'cash' | 'qr';
        note?: string;
    }): Promise<ApiResponse<Transaction>> {
        return this.request<Transaction>('/api/transactions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // =====================
    // SUBSCRIPTION METHODS
    // =====================

    async getSubscription(lineUserId: string): Promise<SubscriptionStatus> {
        const result = await this.request<SubscriptionStatus>(`/api/subscription`, {
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
        const result = await this.request<{ plans: SubscriptionPlan[] }>(`/api/subscription/plans`);
        if (result.error) throw new Error(result.error);
        return result.data!.plans;
    }

    async cancelSubscription(lineUserId: string): Promise<User> {
        const result = await this.request<User>(`/api/subscription/cancel`, {
            method: 'POST',
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    // =====================
    // PAYMENT METHODS
    // =====================

    async createPayment(lineUserId: string, tier: SubscriptionTier): Promise<{
        success: boolean;
        payment_id: number;
        invoice_number: string;
        checkout_url?: string;
        qr_code_url?: string;
        expires_at?: string;
        fallback?: boolean;
        error?: string;
        message?: string;
    }> {
        const result = await this.request<any>(`/api/payment/create`, {
            method: 'POST',
            headers: { 'X-Line-User-Id': lineUserId },
            body: JSON.stringify({ tier }),
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async getPaymentStatus(lineUserId: string, paymentId: number): Promise<{
        id: number;
        amount: number;
        tier: string;
        status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
        invoice_number: string;
        payment_method: string | null;
        created_at: string;
        paid_at: string | null;
        expires_at: string | null;
    }> {
        const result = await this.request<any>(`/api/payment/${paymentId}`, {
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    // =====================
    // STAFF METHODS
    // =====================

    async getStaffList(lineUserId: string): Promise<StaffListResponse> {
        const result = await this.request<StaffListResponse>(`/api/staff/list`, {
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async createInvite(lineUserId: string): Promise<InviteResponse> {
        const result = await this.request<InviteResponse>(`/api/staff/invite`, {
            method: 'POST',
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async joinStore(token: string, lineIdToken: string): Promise<JoinStoreResponse> {
        const result = await this.request<JoinStoreResponse>(`/api/staff/join`, {
            method: 'POST',
            body: JSON.stringify({ token, lineIdToken }),
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async removeStaff(ownerLineUserId: string, staffLineUserId: string): Promise<{ success: boolean }> {
        const result = await this.request<{ success: boolean }>(`/api/staff/${staffLineUserId}`, {
            method: 'DELETE',
            headers: { 'X-Line-User-Id': ownerLineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async leaveStore(lineUserId: string): Promise<{ success: boolean }> {
        const result = await this.request<{ success: boolean }>(`/api/staff/leave`, {
            method: 'POST',
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }

    async getStoreInfo(lineUserId: string): Promise<StoreInfoResponse> {
        const result = await this.request<StoreInfoResponse>(`/api/staff/store-info`, {
            headers: { 'X-Line-User-Id': lineUserId },
        });
        if (result.error) throw new Error(result.error);
        return result.data!;
    }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);

// Export class for testing
export { ApiClient };
